import { type HomeyAPI } from 'homey-api';
import { shallow } from 'zustand/shallow';

import { formatBytes } from '../../../lib/formatBytes';

import { BaseApiStore, type FetchArgs } from '../../../store/BaseApiStore.ts';
import { selectBase } from '../../../store/selectors.ts';
import { useAttach } from '../../../store/useAttach';

type StorageInfoItem = { name: string; size: number };
type StorageInfo = { free: number; total: number; types: { [key: string]: StorageInfoItem } };

interface State {
  storageInfoData: object | null;
  free: number;
  freeFormatted: string;
  total: number;
  totalFormatted: string;
  items: (StorageInfoItem & { key: string; sizeFormatted: string; color: string })[];
  isLoading: boolean;
  error: unknown;
}

type GeneralSettingsInfoStorageStoreState = State;

function getColorForIndex(index: number) {
  let hue = index * 50;
  hue += 150;
  hue %= 360;

  return `hsl(${hue}, 80%, 70%)`;
}

class GeneralSettingsInfoStorageStore extends BaseApiStore<State> {
  constructor() {
    super('general-settings-info-storage');
  }

  override createInitialState() {
    return {
      storageInfoData: null,
      free: 0,
      freeFormatted: '',
      total: 0,
      totalFormatted: '',
      items: [],
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      // Apply 30s timeout instead of 10s as this can take longer on busy Homeys
      // @ts-expect-errorerror TS2554: Expected 0 arguments, but got 1
      const storageInfoData = (await api.system.getStorageInfo({
        $timeout: 30000,
      })) as unknown as StorageInfo;

      if (abortController.signal.aborted) {
        return;
      }

      const free = storageInfoData.free ?? 0;
      const total = storageInfoData.total ?? 0;

      const items = Object.entries(storageInfoData.types ?? {})
        .sort(([, a], [, b]) => b.size - a.size)
        .map(([key, value], index) => {
          return {
            ...value,
            key,
            sizeFormatted: formatBytes(value.size),
            color: getColorForIndex(index),
          };
        });

      this.set({
        ...this.createInitialState(),
        storageInfoData,
        free,
        freeFormatted: formatBytes(free),
        total,
        totalFormatted: formatBytes(total),
        items,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy() {}

  override onAttached({ api }: { api: HomeyAPI }): void {}

  override onDetached({ api }: { api: HomeyAPI }): void {}

  use() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAttach(this, 'general-settings-info-storage');

    return { generalSettingsInfoStorage: this.store(selectAll, shallow) };
  }
}

function selectAll(state: GeneralSettingsInfoStorageStoreState) {
  return {
    ...selectBase(state),
    storageInfoData: state.storageInfoData,
    free: state.free,
    freeFormatted: state.freeFormatted,
    total: state.total,
    totalFormatted: state.totalFormatted,
    items: state.items,
  };
}

export const generalSettingsInfoStorageStore = new GeneralSettingsInfoStorageStore();
